import { forwardRef, useState } from 'react';
import {
  Button as AriaButton,
  Input as AriaInput,
  Popover as AriaPopover,
  SearchField as AriaSearchField,
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  SelectValue as AriaSelectValue,
  Autocomplete,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  useFilter,
} from 'react-aria-components';

import styled, { css } from 'styled-components';

import { media } from '../../themes/signup';
import { StyledCheckIcon } from '../Icons/Check';
import { ChevronDownIcon } from '../Icons/ChevronDown';
import { Background } from './common/Background';
import { Container } from './common/Container';
import { INPUT_PADDING } from './common/InputPadding';
import { ErrorLabel, Label } from './common/Label';

const StyledSelect = styled(AriaSelect)`
  width: 100%;
  position: relative;
`;

const Button = styled(AriaButton)(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    background: unset;
    border: 0;
    outline: 0;
    padding: 0 10px 0 0;
    width: 100%;
    height: 48px;
    color: ${theme.colors.white};
    font-size: ${theme.fontSizes.small};
    line-height: 170%;

    ${media.xlarge`
        height: 60px;
        font-size: ${theme.fontSizes.medium};
    `}

    .icon {
      display: grid;
      place-items: center;
    }

    svg path {
      fill: ${theme.colors.white};
    }
  `,
);

const SelectValue = styled(AriaSelectValue)`
  padding-top: ${INPUT_PADDING};
  padding-left: ${INPUT_PADDING};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  width: calc(100% - 40px);
`;

const Popover = styled(AriaPopover)(
  ({ theme }) => css`
    width: var(--trigger-width);
    border-radius: 12px;
    padding: 0.25rem;
    background: ${theme.colors.grayDark};
    color: ${theme.colors.white};
    box-shadow: 0 2px 12px ${theme.colors.background};
    outline: 1px solid ${theme.colors.grayMedium};
    overflow-y: auto;
    scroll-padding-bottom: 0.5rem;

    /* Style scrollbar to match container */
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.grayMedium} transparent;

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.grayMedium};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${theme.colors.grayDark};
    }

    .select-item {
      padding: 10px 12px;
      border-radius: 8px;
      display: flex;
      justify-content: space-between;
    }

    .select-item.focused {
      background: ${theme.colors.grayDarkest};
    }

    .select-item.selected {
      color: ${theme.colors.grayDarkest};
      background: ${theme.colors.grayNeutral};

      svg path {
        fill: ${theme.colors.grayDarkest};
      }
    }
  `,
);

const SearchField = styled(AriaSearchField)(
  ({ theme }) => css`
    position: sticky;
    top: 0.25rem;
    left: 0.25rem;
    right: 0.25rem;
    display: flex;
    align-items: center;
    background: ${theme.colors.grayDark};
    border-radius: 10px;
    margin: 0 0 0.75rem;
    padding: 0;
    box-shadow: 0 -24px 0 30px ${theme.colors.grayDark};
    outline: 1px solid ${theme.colors.grayMedium};

    &:focus-within {
      outline: 1px solid ${theme.colors.helsinkiBlue};
      outline-offset: -1px;
    }
  `,
);

const Input = styled(AriaInput)(
  ({ theme }) => css`
    padding: 10px 12px;
    flex: 1;
    height: 40px;
    min-width: 0;
    border: none;
    font-size: 1rem;
    background: transparent;
    color: ${theme.colors.white};

    &::placeholder {
      color: ${theme.colors.grayLight};
      font-size: 0.875em;
    }

    &::-webkit-search-cancel-button {
      display: none;
    }

    &:focus {
      outline: none;
    }
  `,
);

const EmptyState = styled.div(
  ({ theme }) => css`
    padding: 10px 12px;
    min-height: 1rem;
    color: ${theme.colors.grayLight};
    text-align: center;
  `,
);

interface SelectItem {
  id: string;
  label: string;
}

interface SelectProps extends Omit<AriaSelectProps<SelectItem>, 'children' | 'placeholder'> {
  label: string;
  error?: string;
  items?: Iterable<SelectItem>;
  search?: {
    placeholder: string;
    emptyText: string;
  };
  children: React.ReactNode | ((item: SelectItem) => React.ReactNode);
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({ label, error, items, search, onSelectionChange, children, ...props }: SelectProps, ref) => {
    const { isInvalid, name } = props;
    const [hasValue, setHasValue] = useState(!!props.selectedKey);

    const filter = useFilter({ sensitivity: 'base' });
    const contains = (value: string, search: string) => filter.contains(value, search);

    const classes = ['select'];
    if (hasValue) {
      classes.push('has-value');
    }

    return (
      <Container>
        <Background $isError={isInvalid}>
          <StyledSelect
            ref={ref}
            {...props}
            className={classes.join(' ')}
            name={name}
            placeholder=""
            aria-labelledby={name + '-label'}
            onSelectionChange={(key) => {
              onSelectionChange?.(key);
              setHasValue(true);
            }}
          >
            <Button>
              <SelectValue />
              <span className="icon" aria-hidden="true">
                <ChevronDownIcon />
              </span>
            </Button>
            <Popover>
              {search ? (
                <Autocomplete filter={contains}>
                  <SearchField
                    aria-label="Search"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  >
                    <Input placeholder={search.placeholder} />
                  </SearchField>
                  <ListBox items={items} renderEmptyState={() => <EmptyState>{search.emptyText}</EmptyState>}>
                    {children}
                  </ListBox>
                </Autocomplete>
              ) : (
                <ListBox items={items}>{children}</ListBox>
              )}
            </Popover>
          </StyledSelect>
          <Label as="span" id={name + '-label'}>
            {label}
          </Label>
        </Background>
        {isInvalid && <ErrorLabel role="alert">{error}</ErrorLabel>}
      </Container>
    );
  },
);

export function SelectItem({ children, ...props }: ListBoxItemProps & { children: string }) {
  return (
    <ListBoxItem
      {...props}
      textValue={children}
      className={({ isFocused, isSelected }) =>
        `select-item ${isFocused ? 'focused' : ''} ${isSelected ? 'selected' : ''}`
      }
    >
      {({ isSelected }) => (
        <>
          <span>{children}</span>
          {isSelected && (
            <span aria-hidden="true">
              <StyledCheckIcon />
            </span>
          )}
        </>
      )}
    </ListBoxItem>
  );
}
